/** Values must match backend StrEnum values (lowercase snake_case) */
export enum MiniTortStage {
  PROCESSING_SUBMISSION = 'processing_submission',
  REVIEWING_CLAIM = 'reviewing_claim',
  NEED_ADDITIONAL_MATERIALS = 'need_additional_materials',
  RETRIEVING_POLICE_REPORT = 'retrieving_police_report',
  GENERATING_DEMAND = 'generating_demand',
  DEMAND_SUBMITTED = 'demand_submitted',
  INSURER_ACKNOWLEDGED = 'insurer_acknowledged',
  PAYMENT_ISSUED = 'payment_issued',
  PAYMENT_EN_ROUTE = 'payment_en_route',
  CLOSED = 'closed',
  REJECTED = 'rejected',
}

/** Values must match backend StrEnum values (lowercase) */
export enum PrerequisiteStatus {
  PENDING = 'pending',
  EXTRACTED = 'extracted',
  PROCESSED = 'processed',
  COMPLETE = 'complete',
  APPROVED = 'approved',
  FAILED = 'failed',
  MANUAL = 'manual',
  WAIVED = 'waived',
}

/** Values must match backend StrEnum values (lowercase) */
export enum FlagSeverity {
  BLOCKER = 'blocker',
  WARNING = 'warning',
  INFO = 'info',
}

/** Values must match backend StrEnum values (lowercase) */
export enum ContactMethod {
  EMAIL = 'email',
  PHONE = 'phone',
  TEXT = 'text',
}

export interface MiniTortClaim {
  id: string;
  created_at: string;
  modified_at: string;
  matter_id: string;
  claimant_display_name: string;
  accident_date: string | null;
  stage: MiniTortStage;
  stage_entered_at: string;
  sms_consent: boolean;
  email_consent: boolean;
  preferred_contact_method: ContactMethod | null;
  coverage_1p_id: string | null;
  coverage_1p_status: PrerequisiteStatus;
  coverage_1p_satisfied_at: string | null;
  coverage_1p_source: string | null;
  coverage_1p_confidence: number | null;
  coverage_3p_id: string | null;
  coverage_3p_status: PrerequisiteStatus;
  coverage_3p_satisfied_at: string | null;
  coverage_3p_source: string | null;
  coverage_3p_confidence: number | null;
  police_report_id: string | null;
  police_report_status: PrerequisiteStatus;
  police_report_satisfied_at: string | null;
  police_report_source: string | null;
  processing_status: PrerequisiteStatus;
  processing_completed_at: string | null;
  review_status: PrerequisiteStatus;
  review_approved_at: string | null;
  review_approved_by_id: string | null;
  damage_amount: string | null;
  damage_description: string | null;
  missing_item_prompt: string | null;
  rejection_reason: string | null;
  injury_detected: boolean;
  injury_detected_at: string | null;
  converted_intake_id: string | null;
}

export interface MiniTortSubmissionAttachment {
  id: string;
  attachment_type: string;
  status: string;
  original_filename: string | null;
  content_type: string | null;
  file_size_bytes: number | null;
  blob_id: string | null;
}

export interface MiniTortSubmission {
  id: string;
  claimant_first_name: string;
  claimant_last_name: string;
  claimant_email: string | null;
  claimant_phone: string | null;
  preferred_contact_method: ContactMethod | null;
  accident_date: string | null;
  accident_location: string | null;
  at_fault: string | null;
  deductible_paid: string | null;
  is_registered_owner_and_named_insured: boolean | null;
  accident_within_last_year: boolean | null;
  report_number: string | null;
  police_department: string | null;
  drivers_license_number: string | null;
  date_of_birth: string | null;
  authorized_mal_to_obtain: boolean | null;
  was_injured: boolean | null;
  wants_attorney_consult: boolean | null;
  submitted_at: string;
  source: string | null;
  form_version: string | null;
  external_submission_id: string | null;
  attachments: MiniTortSubmissionAttachment[];
}

export interface ChaseProgress {
  current_step: number;
  total_steps: number;
  last_step_at: string | null;
  next_step_at: string | null;
}

export interface ChaseWorkflowStatus {
  claim_id: string;
  current_step_index: number;
  total_steps: number;
  next_follow_up_at: string | null;
  stopped: boolean;
  completed: boolean;
}

export interface MiniTortClaimDetail extends MiniTortClaim {
  submission: MiniTortSubmission | null;
  chase_progress: ChaseProgress | null;
  demand_letter_status: string | null;
}

export interface MiniTortClaimFlag {
  id: string;
  created_at: string;
  claim_id: string;
  flag_type: string;
  severity: FlagSeverity;
  source: string;
  message: string;
  resolved: boolean;
  resolved_by_id: string | null;
  resolved_at: string | null;
  resolved_reason: string | null;
  attachment_id: string | null;
}

export interface PrerequisiteSummary {
  processing_status: PrerequisiteStatus;
  review_status: PrerequisiteStatus;
  coverage_1p_status: PrerequisiteStatus;
  coverage_3p_status: PrerequisiteStatus;
  police_report_status: PrerequisiteStatus;
  all_ready: boolean;
  ready_for_demand: boolean;
}

export interface PipelineStatus {
  claim_id: string;
  stage: MiniTortStage;
  prerequisites: PrerequisiteSummary;
  has_unresolved_blockers: boolean;
  blocker_count: number;
  warning_count: number;
  chase_progress: ChaseWorkflowStatus | null;
  auto_close_deadline_at: string | null;
  auto_close_on_hold: boolean;
  converted_intake_id: string | null;
}

export interface MiniTortTimelineEntry {
  event_type: string;
  timestamp: string;
  old_stage: MiniTortStage | null;
  new_stage: MiniTortStage | null;
  description: string;
  metadata: Record<string, string>;
}

export interface MiniTortClaimListResponse {
  results: MiniTortClaim[];
  count: number;
}

export interface MiniTortClaimListParams {
  stage?: string;
  injury_detected?: boolean;
  ordering?: string;
  limit?: number;
  offset?: number;
}

/** Backend expects `new_stage`, not `stage` */
export interface TransitionPayload {
  new_stage: MiniTortStage;
  missing_item_prompt?: string;
  rejection_reason?: string;
}

export interface ResolveFlagPayload {
  resolved_reason: string;
}

export interface MiniTortClaimUpdatePayload {
  sms_consent?: boolean;
  email_consent?: boolean;
  preferred_contact_method?: ContactMethod;
  damage_amount?: string;
  damage_description?: string;
  injury_detected?: boolean;
}

/** Ordered stage progression for the stepper (excludes REJECTED terminal) */
export const STAGE_ORDER: MiniTortStage[] = [
  MiniTortStage.PROCESSING_SUBMISSION,
  MiniTortStage.REVIEWING_CLAIM,
  MiniTortStage.NEED_ADDITIONAL_MATERIALS,
  MiniTortStage.RETRIEVING_POLICE_REPORT,
  MiniTortStage.GENERATING_DEMAND,
  MiniTortStage.DEMAND_SUBMITTED,
  MiniTortStage.INSURER_ACKNOWLEDGED,
  MiniTortStage.PAYMENT_ISSUED,
  MiniTortStage.PAYMENT_EN_ROUTE,
  MiniTortStage.CLOSED,
];

/** Stages considered terminal */
export const TERMINAL_STAGES = new Set([
  MiniTortStage.CLOSED,
  MiniTortStage.REJECTED,
]);

/** Prerequisite statuses that count as "satisfied". Use isSatisfied() from utils.ts for checks. */
export const SATISFIED_STATUSES = new Set<PrerequisiteStatus | string>([
  PrerequisiteStatus.EXTRACTED,
  PrerequisiteStatus.PROCESSED,
  PrerequisiteStatus.COMPLETE,
  PrerequisiteStatus.APPROVED,
  PrerequisiteStatus.MANUAL,
  PrerequisiteStatus.WAIVED,
]);
